import { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Face, Original, OriginalSort, Swap } from '../ApiTypes';
import { getLibrary } from '../api';
import { getFaceCroppedSrc } from '../Storage';
import PillTabs from '../components/PillTabs';
import SwapItem from '../components/SwapItem';
import { useUserStateContext } from '../contexts/UserStateContext';
import { groupOriginals, matchesOriginalQuery, OriginalGroup, sortOriginalGroups } from '../originals';
import { CenterContainer } from '../styles/Containers';
import { SearchInput } from '../styles/Inputs';
import { LoadingMessage } from '../styles/Messages';
import { removeDuplicates, useAsyncEffect } from '../utils';

type Tab = "videos" | "faces";

/** 顔の識別単位。同じ写真を上げ直すと `Face` は増えるが `hash` は同じ。 */
const faceKeyOf = (face: Face) => face.hash ?? face.faceId;

const PageContainer = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 30px 30px 100px 30px;

  @media (max-width: 560px) {
    padding: 20px 16px 60px 16px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 6px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;

  @media (max-width: 560px) {
    font-size: 20px;
  }
`;

const PageNote = styled.div`
  font-size: 13px;
  color: #8a8a8a;
  margin-bottom: 20px;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  max-width: 560px;
`;

const TextButton = styled.button`
  padding: 0px;
  border: none;
  background: none;
  color: rgb(254, 167, 0);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
`;

const VideoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`;

const FaceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background-color: #262626;
  border: 1px solid #333333;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardBody = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const VideoThumbnail = styled.img`
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 8px;
  background-color: #1c1c1c;
`;

const VideoTitle = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: #f0f0f0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  word-break: break-word;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: #8a8a8a;
`;

const FaceImg = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  background-color: #1c1c1c;
  cursor: pointer;
`;

const DetailHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const DetailThumbnail = styled.img`
  width: 220px;
  border-radius: 10px;
  object-fit: cover;
  background-color: #1c1c1c;
`;

const DetailFaceImg = styled.img`
  width: 140px;
  height: 140px;
  border-radius: 10px;
  object-fit: cover;
  background-color: #1c1c1c;
`;

const DetailBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DetailTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  word-break: break-word;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 6px;
  background-color: rgb(254, 167, 0);
  color: black;
  font-size: 14px;
  font-weight: bold;
  padding: 10px 16px;
  cursor: pointer;

  &:hover {
    background-color: #f8be53;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #4d4d4d;
  border-radius: 6px;
  background: none;
  color: #d0d0d0;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 16px;
  cursor: pointer;

  &:hover {
    border-color: #6d6d6d;
    color: #ffffff;
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #cfcfcf;
  margin: 24px 0px 12px 0px;
`;

const SwapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`;

const NoResult = styled.div`
  color: #8a8a8a;
`;

// 一度に描く件数。素材は 1,700 件を超える人もいるため全件は置かない。
const PAGE_SIZE = 60;

const MoreButton = styled.button`
  display: block;
  margin: 20px auto 0px auto;
  border: 1px solid #4d4d4d;
  border-radius: 999px;
  background: none;
  color: #d0d0d0;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 20px;
  cursor: pointer;

  &:hover {
    border-color: #6d6d6d;
    color: #ffffff;
  }
`;

const LetsCreate = styled.div`
  font-size: 20px;
  color: rgb(254, 167, 0);
  margin-top: 30px;
`;

const SORT_OPTIONS: { value: OriginalSort, label: string }[] = [
  { value: "lastUsed", label: "Latest used" },
  { value: "newest", label: "Latest added" },
  { value: "oldest", label: "Oldest added" },
];

const TAB_OPTIONS: { value: Tab, label: string }[] = [
  { value: "videos", label: "Videos" },
  { value: "faces", label: "Faces" },
];

/**
 * 素材ライブラリ。作った動画ではなく、**作るための素材**を管理する面。
 *
 * 素材そのものは消せない。生成済みの動画が `original_url` / `face_id` で
 * 参照しているため、消すと過去の動画のサムネイルとタイトルが壊れる。
 */
const LibraryPage: FC<{}> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userState = useUserStateContext();
  const [tab, setTab] = useState<Tab>("videos");
  const [sort, setSort] = useState<OriginalSort>("lastUsed");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedUrl, setSelectedUrl] = useState<string|null>(null);
  const [selectedFaceKey, setSelectedFaceKey] = useState<string|null>(null);

  const [originals, setOriginals] = useState<Original[]|null>(null);
  const [faces, setFaces] = useState<Face[]|null>(null);
  const [swaps, setSwaps] = useState<Swap[]>([]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [tab, search, sort]);

  useAsyncEffect(async () => {
    if (!userState.isSigned) {
      return;
    }
    const library = await getLibrary();
    setOriginals(library.originals);
    setFaces(library.faces);
    setSwaps(library.swaps);
  }, [userState]);

  if (!userState.isSigned) {
    return <CenterContainer>
      <Trans>Log in to manage your videos and faces.</Trans><br />
      <Link to='/login'><LetsCreate><Trans>Login</Trans></LetsCreate></Link>
    </CenterContainer>;
  }

  if (!originals || !faces) {
    return <PageContainer><LoadingMessage /></PageContainer>;
  }

  const groups = groupOriginals(originals);
  const uniqueFaces = removeDuplicates(faces.filter(face => face.hash), faceKeyOf);

  // その素材で作った動画。顔は `hash` 単位で見る（上げ直した分も同じ顔とみなす）。
  const swapsOfUrl = (url: string) => swaps.filter(swap => swap.original?.url === url);
  const swapsOfFaceKey = (key: string) => swaps.filter(swap => swap.face && faceKeyOf(swap.face) === key);

  const selectedGroup = selectedUrl ? groups.find(group => group.url === selectedUrl) : undefined;
  const selectedFace = selectedFaceKey ? uniqueFaces.find(face => faceKeyOf(face) === selectedFaceKey) : undefined;

  if (selectedGroup) {
    return <PageContainer>
      <TextButton onClick={() => setSelectedUrl(null)}>← <Trans>Back to library</Trans></TextButton>
      <DetailHeader style={{ marginTop: 16 }}>
        <DetailThumbnail src={selectedGroup.imageUrl} />
        <DetailBody>
          <DetailTitle>{selectedGroup.title}</DetailTitle>
          <CardMeta>
            <Trans>Used</Trans> {selectedGroup.usedCount}
          </CardMeta>
          <Actions>
            <PrimaryButton onClick={() => navigate(`/create?originalUrl=${encodeURIComponent(selectedGroup.url)}`)}>
              <Trans>Create with this video</Trans>
            </PrimaryButton>
          </Actions>
        </DetailBody>
      </DetailHeader>
      <SectionTitle><Trans>Videos made with this</Trans></SectionTitle>
      {swapsOfUrl(selectedGroup.url).length > 0
        ? <SwapGrid>
            {swapsOfUrl(selectedGroup.url).map(swap => <SwapItem key={swap.swapId} swap={swap} />)}
          </SwapGrid>
        : <NoResult><Trans>You have not made any videos with this yet.</Trans></NoResult>}
    </PageContainer>;
  }

  if (selectedFace) {
    const key = faceKeyOf(selectedFace);
    return <PageContainer>
      <TextButton onClick={() => setSelectedFaceKey(null)}>← <Trans>Back to library</Trans></TextButton>
      <DetailHeader style={{ marginTop: 16 }}>
        <DetailFaceImg src={getFaceCroppedSrc(selectedFace)} />
        <DetailBody>
          <CardMeta>
            <Trans>Videos</Trans> {swapsOfFaceKey(key).length}
          </CardMeta>
          <Actions>
            <PrimaryButton onClick={() => navigate(`/create?faceId=${selectedFace.faceId}`)}>
              <Trans>Create with this face</Trans>
            </PrimaryButton>
          </Actions>
        </DetailBody>
      </DetailHeader>
      <SectionTitle><Trans>Videos made with this</Trans></SectionTitle>
      {swapsOfFaceKey(key).length > 0
        ? <SwapGrid>
            {swapsOfFaceKey(key).map(swap => <SwapItem key={swap.swapId} swap={swap} />)}
          </SwapGrid>
        : <NoResult><Trans>You have not made any videos with this yet.</Trans></NoResult>}
    </PageContainer>;
  }

  const matchedGroups = sortOriginalGroups(
    groups.filter(group => matchesOriginalQuery(group, search)), sort);
  const matchedFaces = uniqueFaces;
  const shownGroups = matchedGroups.slice(0, visibleCount);
  const shownFaces = matchedFaces.slice(0, visibleCount);
  const hiddenCount = tab === "videos"
    ? matchedGroups.length - shownGroups.length
    : matchedFaces.length - shownFaces.length;

  return <PageContainer>
    <PageHeader>
      <PageTitle><Trans>Library</Trans></PageTitle>
    </PageHeader>
    <Controls>
      <PillTabs value={tab} options={TAB_OPTIONS} onChange={value => setTab(value)} />
      {tab === "videos" && <>
        <SearchInput
          value={search}
          placeholder={t("Search by title")}
          onChange={event => setSearch(event.target.value)} />
        <PillTabs value={sort} options={SORT_OPTIONS} onChange={value => setSort(value)} />
      </>}
    </Controls>

    {tab === "videos" && (shownGroups.length > 0
      ? <VideoGrid>
          {shownGroups.map(group => <VideoCard
            key={group.url}
            group={group}
            swapCount={swapsOfUrl(group.url).length}
            onSelect={() => setSelectedUrl(group.url)} />)}
        </VideoGrid>
      : <NoResult>
          <Trans>You have not yet imported videos</Trans>
        </NoResult>)}

    {tab === "faces" && (shownFaces.length > 0
      ? <FaceGrid>
          {shownFaces.map(face => <FaceCard
            key={faceKeyOf(face)}
            face={face}
            swapCount={swapsOfFaceKey(faceKeyOf(face)).length}
            onSelect={() => setSelectedFaceKey(faceKeyOf(face))} />)}
        </FaceGrid>
      : <NoResult>
          <Trans>You have not yet uploaded face photos</Trans>
        </NoResult>)}

    {hiddenCount > 0 && <MoreButton onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}>
      <Trans>Show more</Trans> ({hiddenCount})
    </MoreButton>}
  </PageContainer>;
};

const VideoCard: FC<{
  group: OriginalGroup,
  swapCount: number,
  onSelect(): void,
}> = (props) => {
  return <Card>
    <CardBody onClick={() => props.onSelect()}>
      <VideoThumbnail src={props.group.imageUrl} />
      <VideoTitle>{props.group.title}</VideoTitle>
      <CardMeta>
        <Trans>Used</Trans> {props.group.usedCount} / <Trans>Videos</Trans> {props.swapCount}
      </CardMeta>
    </CardBody>
  </Card>;
};

const FaceCard: FC<{
  face: Face,
  swapCount: number,
  onSelect(): void,
}> = (props) => {
  return <Card>
    <FaceImg src={getFaceCroppedSrc(props.face)} onClick={() => props.onSelect()} />
    <CardMeta><Trans>Videos</Trans> {props.swapCount}</CardMeta>
  </Card>;
};

export default LibraryPage;
